import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Patients from './pages/Patients';
import PatientProfile from './pages/PatientProfile';
import AddClinicalNote from './pages/AddClinicalNote';
import Reports from './pages/Reports';
import ReportView from './pages/ReportView';
import ReportEdit from './pages/ReportEdit';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEffect } from "react";
import SuperAdminDashboard from "./components/Dashboard/SuperAdminDashboard";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Doctors from "./pages/Doctor";
import Users from "./pages/Users";
import ForgotPassword from "./pages/ForgotPassword";
// import VerifyOtp from "./pages/VerifyOtp";
import ResetPassword from "./pages/ResetPassword";
import SuperAdminGuard from "./components/guards/SuperAdminGuard";
import AuditActivityPage from "./pages/AuditActivityPage";
import RoleProtectedRoute from "./components/guards/RoleProtectedRoute";
import { ROLES } from "./constants/roles";



// Normalizes any route to kebab-case, but DO NOT normalize /login to avoid refresh
const RouteNormalizer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathname = location.pathname;

    // Prevent refresh during login, register and password reset
    if (pathname === "/login" || pathname === "/admin/login" || pathname === "/register" || pathname === "/set-password") return;

    const normalizedPath = pathname
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/[_\s]+/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase();

    if (normalizedPath !== pathname) {
      navigate(normalizedPath, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

// Protected Route - requires authentication and validation
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is not validated (false or undefined), redirect to set-password page
  if (user.isValidated !== true) {
    return <Navigate to="/set-password" replace />;
  }

  return <>{children}</>;
};

// // Password Reset Route - requires authentication but allows unvalidated users (unused, kept for reference)
// const PasswordResetRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   const { user, isLoading } = useAuth();
//
//   if (isLoading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center">
//         <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
//       </div>
//     );
//   }
//
//   if (!user) {
//     return <Navigate to="/login" replace />;
//   }
//
//   // If user is already validated, redirect to dashboard
//   if (user.isValidated === true) {
//     return <Navigate to="/dashboard" replace />;
//   }
//
//   return <>{children}</>;
// };

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation(); // SAFE: Hook at top-level always

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If logged in AND validated AND user is on /login, /admin/login or /register → Redirect to dashboard
  // Don't redirect if user is not validated (they need to set password first)
  if (user && user.isValidated === true && (location.pathname === "/login" || location.pathname === "/admin/login" || location.pathname === "/register")) {
    return <Navigate to="/dashboard" replace />;
  }
  

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <RouteNormalizer />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          theme="light"
          closeButton
          className="diag-container"
        />

        <Routes>
          {/* Public Login */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Admin Login */}
          <Route
            path="/admin/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Public Register */}
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />

          {/* Set Password - Requires auth but allows unvalidated users (unused, kept for reference) */}
          {/* <Route
            path="/set-password"
            element={
              <PasswordResetRoute>
                <SetPassword />
              </PasswordResetRoute>
            }
          /> */}

          {/* Super Admin Dashboard */}
          <Route
            path="/set-passwordin-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <SuperAdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Consultant Dashboard */}
          {/* <Route
            path="/consultant-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <ConsultantDashboard />
                </Layout>
              </ProtectedRoute>
            }
          /> */}

          {/* Junior Doctor Dashboard */}
          {/* <Route
            path="/junior-doctor-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <JuniorDoctorDashboard />
                </Layout>
              </ProtectedRoute>
            }
          /> */}

          {/* Default Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>  
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Profile */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route path="/forgot-password" element={<ForgotPassword />} />
          {/* <Route path="/verify-otp" element={<VerifyOtp />} /> */}
          <Route path="/reset-password" element={<ResetPassword />} />


          {/* Doctors - Super Admin Only */}
          <Route
            path="/doctors"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <Doctors />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Users - Super Admin Only */}
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <SuperAdminGuard>
                  <Layout>
                    <Users />
                  </Layout>
                </SuperAdminGuard>
              </ProtectedRoute>
            }
          />

          {/* Other existing protected pages */}
          <Route
  path="/patients"
  element={
    <ProtectedRoute>
      <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
        <Layout>
          <Patients />
        </Layout>
      </RoleProtectedRoute>
    </ProtectedRoute>
  }
/>
<Route
  path="/patients/:id"
  element={
    <ProtectedRoute>
      <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
        <Layout>
          <PatientProfile />
        </Layout>
      </RoleProtectedRoute>
    </ProtectedRoute>
  }
/>
<Route
  path="/patients/:id/notes/new"
  element={
    <ProtectedRoute>
      <RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}>
        <Layout>
          <AddClinicalNote />
        </Layout>
      </RoleProtectedRoute>
    </ProtectedRoute>
  }
/>


          {/* <Route path="/patients" element={<ProtectedRoute><Layout><RoleProtectedRoute allowedRoles={[ROLES.DOCTOR]}><Patients /> </RoleProtectedRoute></Layout></ProtectedRoute>} /> */}
          {/* <Route path="/patients/:id" element={<ProtectedRoute><Layout><PatientProfile /></Layout></ProtectedRoute>} /> */}
          {/* <Route path="/patients/:id/notes/new" element={<ProtectedRoute><Layout><AddClinicalNote /></Layout></ProtectedRoute>} /> */}
          <Route path="/reports" element={<ProtectedRoute><Layout><Reports /></Layout></ProtectedRoute>} />
          <Route path="/reports/:id" element={<ProtectedRoute><Layout><ReportView /></Layout></ProtectedRoute>} />
          <Route path="/reports/:id/edit" element={<ProtectedRoute><Layout><ReportEdit /></Layout></ProtectedRoute>} />
          <Route path="/doctors" element={<SuperAdminGuard><ProtectedRoute><Layout><Doctors /></Layout></ProtectedRoute></SuperAdminGuard>} />
          {/* <Route path="/activity" element={<SuperAdminGuard><ProtectedRoute><Layout><AuditActivityPage /></Layout></ProtectedRoute></SuperAdminGuard>} /> */}
          {/* <Route path="/imaging" element={<ProtectedRoute><Layout><Imaging /></Layout></ProtectedRoute>} /> */}
          <Route path="/activity" element={<ProtectedRoute><Layout><AuditActivityPage /></Layout></ProtectedRoute>} />
          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;